/**
 * Currency exchange rate service using Czech National Bank (ČNB) API
 * Provides official exchange rates for converting foreign currencies to CZK
 */

import { apiGet } from './api/client';

interface ExchangeRateResponse {
  data: { rates: Record<string, number> };
}

/**
 * Fetch exchange rates from backend (which proxies ČNB API)
 * @param date - Date in YYYY-MM-DD format
 * @returns Map of currency code to rate per 1 unit
 */
export async function getExchangeRates(date: string): Promise<Map<string, number>> {
  const data = await apiGet<ExchangeRateResponse>(`/exchange-rates?date=${date}`);
  return new Map(Object.entries(data.data.rates));
}

/**
 * Convert amount from foreign currency to CZK
 * @param amount - Amount in foreign currency
 * @param fromCurrency - Currency code (EUR, USD, etc.)
 * @param exchangeRate - Exchange rate (1 unit of foreign currency = X CZK)
 * @returns Amount in CZK
 */
export function convertToCZK(amount: number, fromCurrency: string, exchangeRate: number): number {
  if (fromCurrency === 'CZK') return amount;
  return amount * exchangeRate;
}

/**
 * Common currencies for the selector
 */
export const COMMON_CURRENCIES = [
  { code: 'CZK', name: 'Czech Koruna', symbol: 'Kč' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'PLN', name: 'Polish Zloty', symbol: 'zł' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { code: 'SEK', name: 'Swedish Krona', symbol: 'kr' },
  { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr' },
  { code: 'DKK', name: 'Danish Krone', symbol: 'kr' },
  { code: 'HUF', name: 'Hungarian Forint', symbol: 'Ft' },
];
